import { createContext, useState, useEffect, ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Resource, ResourceKey } from 'i18next'

import Loader from '@/ui/components/Loader/Loader'

import api from '@/utils/api/api'
import utils from '@/utils/utils'
import { initializeI18n } from '@/utils/i18n'
import { RootState } from '@/utils/store/store'
import { updateRawConfig, toggleAppTheme } from '@/utils/store/features/application/applicationSlice'

import { Config, ConfigContextType } from '@/shared/types/config'
import { AssistantConfig, SpaceConfig } from '@/shared/types/space'

const CONFIG_CACHE_KEY = 'mma_config_cache'

// Prepend the Vite base URL to a root-relative path so asset fetches work
// correctly when the app is deployed under a subpath (e.g. /mma/).
const withBase = (path: string) =>
    `${import.meta.env.BASE_URL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`

const hasUnresolvedEnvTemplate = (value: unknown): boolean => {
    if (typeof value === 'string') return value.includes('{{VITE_')
    if (Array.isArray(value)) return value.some(hasUnresolvedEnvTemplate)
    if (value && typeof value === 'object') return Object.values(value).some(hasUnresolvedEnvTemplate)
    return false
}

const ConfigContext = createContext<ConfigContextType>({
    isDarkTheme: false,
    toggleTheme: () => { },
    getConfig: () => undefined,
})

interface TranslationFile {
    id: string
    translations?: Record<string, ResourceKey>
}

const processTranslations = async (
    content: Record<string, () => Promise<unknown>>,
    locales: Resource
) => {
    await Promise.all(Object.entries(content).map(async ([path]) => {
        const fileContent = await api.get<TranslationFile>(withBase(path))
        if (fileContent.translations) {
            for (const lang in fileContent.translations) {
                locales[lang] = {
                    ...locales[lang],
                    [fileContent.id]: fileContent.translations[lang]
                };
            }
        } else {
            const language = path.split('/')[3];
            const id = path.split('/')[4]?.split(".")[0];
            locales[language] = {
                ...locales[language],
                [id]: fileContent
            };
        }
    }))
    return locales;
};

const fetchLocales = async () => {
    const content = import.meta.glob('/configs/locale/*/*');
    const assistantContent = import.meta.glob('/configs/assistantConfigs/*');
    const spaceContent = import.meta.glob('/configs/spaceConfigs/*');
    const locales: Resource = {};

    await Promise.all([
        processTranslations(content, locales),
        processTranslations(assistantContent, locales),
        processTranslations(spaceContent, locales),
    ]);

    return locales;
};

const loadFileCollection = async <T,>(configFiles: Record<string, () => Promise<unknown>>) => {
    return await Promise.all(Object.keys(configFiles).map(async (path) => api.get<T>(withBase(path))));
}

const ConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const config = useSelector((state: RootState) => state.application.config)
    const isDarkTheme = useSelector((state: RootState) => state.application.darkTheme)

    const [isConfigLoaded, setIsConfigLoaded] = useState(false)

    // app slice sets language var
    const dispatch = useDispatch()

    useEffect(() => {
        const loadFiles = async () => {
            const cached = sessionStorage.getItem(CONFIG_CACHE_KEY)
            if (cached) {
                try {
                    const cachedConfig = JSON.parse(cached)
                    if (hasUnresolvedEnvTemplate(cachedConfig)) {
                        sessionStorage.removeItem(CONFIG_CACHE_KEY)
                        throw new Error('Cached config contains unresolved environment template values')
                    }
                    const cachedLocales = cachedConfig._locales
                    delete cachedConfig._locales
                    document.title = cachedConfig?.name || 'MMA'
                    initializeI18n(cachedLocales)
                    dispatch(updateRawConfig({ rawConfig: cachedConfig }))
                    setIsConfigLoaded(true)
                    return
                } catch {
                    sessionStorage.removeItem(CONFIG_CACHE_KEY)
                }
            }

            const spaceConfigFiles = import.meta.glob('/configs/spaceConfigs/*')
            const assistantConfigFiles = import.meta.glob('/configs/assistantConfigs/*')
            const [spaceConfigs, assistantConfigs] = await Promise.all([
                loadFileCollection<SpaceConfig>(spaceConfigFiles),
                loadFileCollection<AssistantConfig>(assistantConfigFiles),
            ])

            assistantConfigs.sort((a, b) => {
                const aEndpoint = a.endpoint || '';
                const bEndpoint = b.endpoint || '';

                const aIsOpra = aEndpoint.includes('issue-documentation-opra');
                const bIsOpra = bEndpoint.includes('issue-documentation-opra');
                if (aIsOpra && !bIsOpra) return 1;
                if (!aIsOpra && bIsOpra) return -1;

                if (aEndpoint.includes('issue-resolution') && !bEndpoint.includes('issue-resolution')) {
                    return -1;
                }
                if (!aEndpoint.includes('issue-resolution') && bEndpoint.includes('issue-resolution')) {
                    return 1;
                }

                if (aEndpoint.includes('issue-documentation') && !bEndpoint.includes('issue-documentation')) {
                    return -1;
                }
                if (!aEndpoint.includes('issue-documentation') && bEndpoint.includes('issue-documentation')) {
                    return 1;
                }

                return 0;
            })

            const [history, locales, rawConfigFile] = await Promise.all([
                api.get<NonNullable<Config['history']>>(withBase('/configs/history.json')),
                fetchLocales(),
                api.get<Config>(withBase('/configs/config.json')),
            ])

            rawConfigFile.history = utils.sortByVersion(history)
            document.title = rawConfigFile?.name || 'ChatXDC'
            initializeI18n(locales)

            const finalConfig = {
                ...rawConfigFile,
                spaces: spaceConfigs,
                assistants: assistantConfigs,
            }

            try {
                sessionStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify({ ...finalConfig, _locales: locales }))
            } catch {
                console.warn('Failed to cache config in sessionStorage')
            }

            dispatch(updateRawConfig({ rawConfig: finalConfig }))
            setIsConfigLoaded(true)
        }

        loadFiles()
    }, [dispatch])

    const toggleTheme = () => {
        dispatch(toggleAppTheme())
    }

    const getConfig = () => {
        return config
    }

    if (!isConfigLoaded)
        return <Loader />

    return (
        <ConfigContext.Provider value={{ isDarkTheme, toggleTheme, getConfig }}>
            {children}
        </ConfigContext.Provider >
    );
};

export { ConfigProvider, ConfigContext }
